import React, { useCallback, useEffect, useMemo, useState } from "react";
import Sidebar from "../Sidebar";
import { environment } from "../../config/environment";
import { getClientEnvironmentId } from "../../config/capabilities";
import { customFetch } from "../../utils/Auth";

const MODEL_TYPES = ["English", "Multilingual"];
const TRANSCRIPTION_MODELS = ["Deepgram", "Whisper"];
const DEEPGRAM_MODELS = ["nova-3", "nova-3-medical", "nova-3-pharma"];
const PAGE_SIZE = 100;

const defaultDiarize = () => getClientEnvironmentId() !== "commercial-prod";

const diarizeForRule = (rule, isNew) => {
  if (typeof rule?.diarize === "boolean") return rule.diarize;
  return isNew ? defaultDiarize() : false;
};

const emptyDraft = {
  user_id: "",
  user_name: "",
  organization: "",
  specialty: "",
  super_org: "",
  model_type: "",
  transcriptionModel: "Deepgram",
  deepgramModel: "nova-3",
};

export default function TranscriptionRules({ setAuth, setUserOrg }) {
  const [rules, setRules] = useState([]);
  const [isLoadingRules, setIsLoadingRules] = useState(true);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageCursors, setPageCursors] = useState([null]);
  const [nextCursor, setNextCursor] = useState(null);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);

  const [allDoctors, setAllDoctors] = useState([]);
  const [doctorSearch, setDoctorSearch] = useState("");
  const [isLoadingDoctor, setIsLoadingDoctor] = useState(false);

  const [draft, setDraft] = useState(null);
  const [isExistingRule, setIsExistingRule] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [dialogError, setDialogError] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  const loadRules = useCallback(async (cursor, index, { commitEmpty = true } = {}) => {
    setIsLoadingRules(true);
    setError(null);
    try {
      const params = new URLSearchParams({ pageSize: String(PAGE_SIZE) });
      if (cursor) params.set("cursor", cursor);
      const response = await customFetch(
        `${environment.apiUrl}/transcription-rules?${params.toString()}`,
        { method: "GET" }
      );
      if (!response.ok) throw new Error("Failed to load transcription rules");
      const data = await response.json();
      const loaded = Array.isArray(data.rules) ? data.rules : [];
      if (!commitEmpty && loaded.length === 0 && index > 0) return loaded;
      setRules(loaded);
      setNextCursor(data.nextCursor || null);
      setPageIndex(index);
      return loaded;
    } catch (err) {
      setError(err.message);
      setRules([]);
      setNextCursor(null);
      return null;
    } finally {
      setIsLoadingRules(false);
    }
  }, []);

  useEffect(() => {
    loadRules(null, 0);
  }, [loadRules]);

  const goToPage = (index) => {
    if (index < 0 || index >= pageCursors.length || isLoadingRules) return;
    loadRules(pageCursors[index], index);
  };

  const goToNextPage = () => {
    if (!nextCursor || isLoadingRules) return;
    const index = pageIndex + 1;
    setPageCursors((current) => {
      const updated = current.slice(0, index);
      updated[index] = nextCursor;
      return updated;
    });
    loadRules(nextCursor, index);
  };

  useEffect(() => {
    (async () => {
      try {
        const response = await customFetch(
          `${environment.apiUrl}/doctors/all-doctors`,
          { method: "GET" }
        );
        const data = await response.json();
        setAllDoctors(Array.isArray(data) ? data : []);
      } catch {
        setAllDoctors([]);
      }
    })();
  }, []);

  const filteredDoctors = useMemo(() => {
    const search = doctorSearch.trim().toLowerCase();
    if (!search) return [];
    return allDoctors
      .filter((doc) => {
        const name = doc.user_name?.toLowerCase() || "";
        const email = doc.user_email?.toLowerCase() || "";
        const id = doc.user_id?.toLowerCase() || "";
        return name.includes(search) || email.includes(search) || id.includes(search);
      })
      .slice(0, 25);
  }, [doctorSearch, allDoctors]);

  const openDraftForDoctor = async (doctor) => {
    setDoctorSearch("");
    setIsLoadingDoctor(true);
    setDialogError(null);
    setStatus(null);
    try {
      const response = await customFetch(
        `${environment.apiUrl}/doctors/doctor-by-user-id/${doctor.user_id}`,
        { method: "GET" }
      );
      if (!response.ok) throw new Error("Failed to load doctor details");
      const full = await response.json();

      const ruleResponse = await customFetch(
        `${environment.apiUrl}/transcription-rules/${encodeURIComponent(doctor.user_id)}`,
        { method: "GET" }
      );
      if (!ruleResponse.ok && ruleResponse.status !== 404) {
        throw new Error("Failed to load the existing transcription rule");
      }
      const existing = ruleResponse.ok ? (await ruleResponse.json()).rule : null;

      setIsExistingRule(Boolean(existing));
      setDraft({
        ...emptyDraft,
        user_id: full.user_id || doctor.user_id,
        user_name: full.user_name || doctor.user_name || "",
        organization: full.organization || "",
        specialty: full.specialty || doctor.specialty || "",
        super_org: full.super_org || doctor.super_org || "",
        model_type: MODEL_TYPES.includes(existing?.model_type)
          ? existing.model_type
          : MODEL_TYPES.includes(full.model_type)
          ? full.model_type
          : "",
        transcriptionModel: existing?.transcriptionModel || "Deepgram",
        deepgramModel: existing?.deepgramModel || "nova-3",
        diarize: diarizeForRule(existing, !existing),
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoadingDoctor(false);
    }
  };

  const openDraftForRule = (rule) => {
    setDialogError(null);
    setStatus(null);
    setIsExistingRule(true);
    setDraft({
      ...emptyDraft,
      ...rule,
      model_type: MODEL_TYPES.includes(rule.model_type) ? rule.model_type : "",
      transcriptionModel: rule.transcriptionModel || "Deepgram",
      deepgramModel: rule.deepgramModel || "nova-3",
      diarize: diarizeForRule(rule, false),
    });
  };

  const saveDraft = async () => {
    if (!draft) return;
    setIsSaving(true);
    setDialogError(null);
    try {
      const response = await customFetch(
        `${environment.apiUrl}/transcription-rules`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(draft),
        }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to save rule");
      setDraft(null);
      setStatus(`Saved transcription rule for ${data.rule.user_name || data.rule.user_id}`);
      await loadRules(pageCursors[pageIndex], pageIndex);
    } catch (err) {
      setDialogError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const deleteRule = async (rule) => {
    setPendingDelete(null);
    setStatus(null);
    try {
      const response = await customFetch(
        `${environment.apiUrl}/transcription-rules/${rule.user_id}`,
        { method: "DELETE" }
      );
      if (!response.ok) throw new Error("Failed to delete rule");
      setStatus(`Removed transcription rule for ${rule.user_name || rule.user_id}`);
      const index = pageIndex;
      const loaded = await loadRules(pageCursors[index], index, { commitEmpty: false });
      if (loaded && loaded.length === 0 && index > 0) {
        setPageCursors((current) => current.slice(0, index));
        await loadRules(pageCursors[index - 1], index - 1);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const isDraftValid =
    draft &&
    draft.user_id &&
    MODEL_TYPES.includes(draft.model_type) &&
    TRANSCRIPTION_MODELS.includes(draft.transcriptionModel) &&
    (draft.transcriptionModel !== "Deepgram" ||
      DEEPGRAM_MODELS.includes(draft.deepgramModel));

  return (
    <div className="flex">
      <div className="h-[100dvh]">
        <div className="flex h-[100%] fixed top-0">
          <Sidebar setAuth={setAuth} setUserOrg={setUserOrg} />
        </div>
      </div>
      <div
        className="flex overflow-x-hidden"
        style={{
          marginLeft: "var(--sidebar-width, 250px)",
          width: "calc(100% - var(--sidebar-width, 250px))",
        }}
      >
        <div className="flex flex-col w-full px-8 pb-16">
          <h1 className="font-bold text-[28px] text-ktx-purple my-[2rem] text-center">
            Transcription Rules
          </h1>

          <div className="relative w-full max-w-[420px] mb-6">
            <label
              htmlFor="doctorSearch"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Add or update a doctor
            </label>
            <input
              id="doctorSearch"
              type="text"
              autoComplete="off"
              value={doctorSearch}
              onChange={(e) => setDoctorSearch(e.target.value)}
              placeholder="Type to search for a doctor..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ktx-purple focus:border-ktx-purple"
            />
            {doctorSearch && (
              <div className="absolute z-20 w-full max-h-56 overflow-y-auto border border-gray-200 rounded-md bg-white shadow mt-1">
                {filteredDoctors.length === 0 ? (
                  <div className="px-4 py-2 text-gray-400">No doctors found.</div>
                ) : (
                  filteredDoctors.map((doc) => (
                    <div
                      key={doc.user_id}
                      className="px-4 py-2 cursor-pointer hover:bg-purple-100"
                      onClick={() => openDraftForDoctor(doc)}
                    >
                      <div className="font-semibold text-gray-900">
                        {doc.user_name}{" "}
                        {doc.user_email ? (
                          <span className="text-xs text-gray-500">({doc.user_email})</span>
                        ) : null}
                      </div>
                      <div className="text-xs text-gray-500">{doc.user_id}</div>
                    </div>
                  ))
                )}
              </div>
            )}
            {isLoadingDoctor && (
              <div className="text-xs text-gray-500 mt-2">Loading doctor details…</div>
            )}
          </div>

          {status && (
            <div className="mb-4 px-4 py-2 rounded-md bg-green-50 text-green-800 text-sm">
              {status}
            </div>
          )}
          {error && (
            <div className="mb-4 px-4 py-2 rounded-md bg-red-50 text-red-800 text-sm">
              {error}
            </div>
          )}

          <div className="border border-gray-200 rounded-lg overflow-x-auto bg-white">
            <table className="min-w-full text-sm text-left">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-4 py-3 font-semibold">Doctor</th>
                  <th className="px-4 py-3 font-semibold">Organization</th>
                  <th className="px-4 py-3 font-semibold">Super Org</th>
                  <th className="px-4 py-3 font-semibold">Specialty</th>
                  <th className="px-4 py-3 font-semibold">Model Type</th>
                  <th className="px-4 py-3 font-semibold">Transcription Model</th>
                  <th className="px-4 py-3 font-semibold">Deepgram Model</th>
                  <th className="px-4 py-3 font-semibold">Diarize</th>
                  <th className="px-4 py-3 font-semibold text-right sticky right-0 bg-gray-50">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoadingRules ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                      Loading rules…
                    </td>
                  </tr>
                ) : rules.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                      {pageIndex === 0
                        ? "No transcription rules yet. Search for a doctor above to add one."
                        : "No rules on this page."}
                    </td>
                  </tr>
                ) : (
                  rules.map((rule) => (
                    <tr key={rule.user_id} className="border-t border-gray-100">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">
                          {rule.user_name || "-"}
                        </div>
                        <div className="text-xs text-gray-500">{rule.user_id}</div>
                      </td>
                      <td className="px-4 py-3">{rule.organization || "-"}</td>
                      <td className="px-4 py-3">{rule.super_org || "-"}</td>
                      <td className="px-4 py-3">{rule.specialty || "-"}</td>
                      <td className="px-4 py-3">{rule.model_type || "-"}</td>
                      <td className="px-4 py-3">{rule.transcriptionModel || "-"}</td>
                      <td className="px-4 py-3">{rule.deepgramModel || "-"}</td>
                      <td className="px-4 py-3">{rule.diarize ? "Yes" : "No"}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap sticky right-0 bg-white">
                        <button
                          type="button"
                          className="px-3 py-1 mr-2 text-ktx-purple border border-ktx-purple rounded hover:bg-purple-50"
                          onClick={() => openDraftForRule(rule)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="px-3 py-1 text-red-600 border border-red-300 rounded hover:bg-red-50"
                          onClick={() => setPendingDelete(rule)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {(pageIndex > 0 || nextCursor) && (
            <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
              <span>
                Page {pageIndex + 1}
                {rules.length > 0 ? ` · ${rules.length} rules` : ""}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40"
                  onClick={() => goToPage(pageIndex - 1)}
                  disabled={pageIndex === 0 || isLoadingRules}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40"
                  onClick={goToNextPage}
                  disabled={!nextCursor || isLoadingRules}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {draft && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
            <h2 className="text-xl font-semibold text-ktx-purple mb-1">
              {isExistingRule ? "Edit" : "Add"} transcription rule
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              {draft.user_name || draft.user_id}
            </p>

            <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
              <ReadOnlyField label="User ID" value={draft.user_id} />
              <ReadOnlyField label="User Name" value={draft.user_name} />
              <ReadOnlyField label="Organization" value={draft.organization} />
              <ReadOnlyField label="Super Org" value={draft.super_org} />
              <ReadOnlyField label="Specialty" value={draft.specialty} />
            </div>

            <div className="space-y-4">
              <SelectField
                label="Model Type"
                value={draft.model_type}
                options={MODEL_TYPES}
                placeholder="Select a model type"
                onChange={(value) => setDraft({ ...draft, model_type: value })}
              />
              <SelectField
                label="Transcription Model"
                value={draft.transcriptionModel}
                options={TRANSCRIPTION_MODELS}
                onChange={(value) =>
                  setDraft({
                    ...draft,
                    transcriptionModel: value,
                    deepgramModel: value === "Deepgram" ? draft.deepgramModel || "nova-3" : "",
                  })
                }
              />
              <SelectField
                label="Diarize"
                value={draft.diarize ? "Yes" : "No"}
                options={["Yes", "No"]}
                onChange={(value) => setDraft({ ...draft, diarize: value === "Yes" })}
              />
              {draft.transcriptionModel === "Deepgram" && (
                <SelectField
                  label="Deepgram Model"
                  value={draft.deepgramModel}
                  options={DEEPGRAM_MODELS}
                  onChange={(value) => setDraft({ ...draft, deepgramModel: value })}
                />
              )}
            </div>

            {dialogError && (
              <div className="mt-4 px-3 py-2 rounded-md bg-red-50 text-red-800 text-sm">
                {dialogError}
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                onClick={() => setDraft(null)}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 bg-ktx-purple text-white rounded hover:bg-purple-700 disabled:opacity-50"
                onClick={saveDraft}
                disabled={!isDraftValid || isSaving}
              >
                {isSaving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Delete transcription rule
            </h2>
            <p className="text-sm text-gray-600">
              {pendingDelete.user_name || pendingDelete.user_id} will fall back to the
              default transcription model.
            </p>
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                onClick={() => setPendingDelete(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                onClick={() => deleteRule(pendingDelete)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ReadOnlyField({ label, value }) {
  return (
    <div>
      <div className="text-xs font-medium text-gray-500 mb-1">{label}</div>
      <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded break-all text-gray-800">
        {value || "-"}
      </div>
    </div>
  );
}

function SelectField({ label, value, options, placeholder, onChange }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ktx-purple focus:border-ktx-purple"
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </div>
  );
}
